import React from 'react';
import DOMPurify from 'dompurify';
import { renderContent } from '../renderContent';
import CourseBlockRenderer, { BLOCK_CONFIG } from './CourseBlockRenderer';

const IFRAME_CONFIG = { ADD_TAGS: ['iframe'], ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'src', 'title'] };

export default function NotionPreview({ blocks }) {
    if (!blocks?.length) return (
        <p className="text-sm text-gray-400 italic text-center py-8">Aucun bloc à prévisualiser.</p>
    );

    let h2 = 0, h3 = 0;

    return (
        <div className="space-y-6">
            {blocks.map(section => {
                const type = section.content_type || section.type || 'text';
                const cfg  = BLOCK_CONFIG[type];

                // ── image_prompt : prompt + média généré si disponible ──
                if (type === 'image_prompt') return (
                    <div key={section.id} className="space-y-3">
                        <div className="border-2 border-dashed border-orange-300 dark:border-orange-600 bg-orange-50 dark:bg-orange-950/20 rounded-lg px-4 py-3">
                            <p className="text-xs font-bold uppercase tracking-wider mb-1 text-orange-700 dark:text-orange-400">📷 Schéma à générer</p>
                            <p className="text-sm text-orange-800 dark:text-orange-300 italic">{section.content}</p>
                        </div>
                        {section.media_url && section.media_type === 'image' && (
                            <img src={section.media_url} alt={section.media_description || ''} className="rounded-2xl w-full max-w-3xl mx-auto shadow-md" />
                        )}
                        {section.media_url && section.media_type === 'video' && (
                            <div className="aspect-video w-full max-w-3xl mx-auto overflow-hidden rounded-2xl shadow-lg bg-black">
                                <video controls className="w-full h-full object-cover" src={section.media_url} />
                            </div>
                        )}
                    </div>
                );

                // ── Image générée ──
                if (type === 'image') return (
                    <div key={section.id} className="my-4">
                        {section.title && <p className="text-sm text-gray-500 mb-2 italic">{section.title}</p>}
                        {section.media_url
                            ? <img src={section.media_url} alt={section.media_description || ''} className="rounded-2xl w-full max-w-3xl mx-auto shadow-md" />
                            : <span className="text-xs text-gray-400">Image non disponible</span>}
                    </div>
                );

                // ── Vidéo générée ──
                if (type === 'video' && section.media_url) return (
                    <div key={section.id} className="my-4">
                        {section.title && <p className="text-sm text-gray-500 mb-2 italic">{section.title}</p>}
                        <div className="aspect-video w-full max-w-3xl mx-auto overflow-hidden rounded-2xl shadow-lg bg-black">
                            <video controls className="w-full h-full object-cover" src={section.media_url} />
                        </div>
                    </div>
                );

                // ── Blocs colorés ──
                if (cfg) return (
                    <div key={section.id} className={`${cfg.border} ${cfg.bg} rounded-r-lg px-5 py-4`}>
                        <div className={`text-xs font-bold uppercase tracking-wider mb-2 ${cfg.color} [&_p]:inline [&_p]:m-0`}>
                            {cfg.label}{section.title && <> — {renderContent(section.title)}</>}
                        </div>
                        <div className="text-gray-800 dark:text-gray-200 leading-relaxed">{renderContent(section.content)}</div>
                    </div>
                );

                // ── Heading numéroté ──
                if (type === 'heading') {
                    const isH3 = /^#{3,}\s/.test(section.content || '');
                    if (isH3) { h3++; } else { h2++; h3 = 0; }
                    const prefix = isH3 ? `${h2}.${h3}` : `${h2}`;
                    const text   = (section.content || '').replace(/^#{1,6}\s+/, '');
                    return (
                        <div key={section.id} className={`flex items-baseline gap-2 ${isH3 ? 'mt-3 ml-4' : 'mt-6'}`}>
                            <span className={`font-mono shrink-0 text-gray-400 dark:text-gray-500 select-none ${isH3 ? 'text-xs' : 'text-sm'}`}>{prefix}</span>
                            <span className={`font-bold text-gray-900 dark:text-white leading-snug ${isH3 ? 'text-base' : 'text-lg'} [&_p]:inline [&_p]:m-0`}>{renderContent(text)}</span>
                        </div>
                    );
                }

                // ── Podcast ──
                if (type === 'podcast') return (
                    <div key={section.id} className="bg-cyan-500/10 border border-cyan-500/30 p-6 rounded-2xl flex flex-col items-center shadow-inner">
                        <span className="text-4xl mb-3">🎧</span>
                        <h3 className="text-lg font-bold text-cyan-800 dark:text-cyan-300 mb-4 text-center [&_p]:inline [&_p]:m-0">{renderContent(section.title)}</h3>
                        <audio controls className="w-full max-w-md rounded-full">
                            <source src={section.content} />
                        </audio>
                    </div>
                );

                // ── Vidéo ──
                if (type === 'video') return (
                    <div key={section.id}>
                        <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-100 [&_p]:inline [&_p]:m-0">{renderContent(section.title)}</h3>
                        <div className="aspect-video w-full max-w-3xl mx-auto overflow-hidden rounded-2xl shadow-lg border border-gray-200 dark:border-white/[0.05] bg-black">
                            {section.content?.includes('<iframe') ? (
                                <div className="w-full h-full [&>iframe]:w-full [&>iframe]:h-full" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.content, IFRAME_CONFIG) }} />
                            ) : (
                                <video controls className="w-full h-full object-cover">
                                    <source src={section.content} />
                                </video>
                            )}
                        </div>
                    </div>
                );

                // ── HTML brut ──
                if (type === 'html') return (
                    <div key={section.id}>
                        <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-100 [&_p]:inline [&_p]:m-0">{renderContent(section.title)}</h3>
                        <div className="text-gray-700 dark:text-gray-300 leading-relaxed text-base space-y-4 bg-gray-50 dark:bg-black/20 p-6 rounded-2xl border border-gray-100 dark:border-white/[0.02]"
                             dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.content) }}
                        />
                    </div>
                );

                // ── ai_draft ──
                if (type === 'ai_draft') return (
                    <div key={section.id}>
                        <CourseBlockRenderer content={section.content} />
                    </div>
                );

                // ── text / paragraph ──
                return (
                    <div key={section.id}>
                        {section.title && <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-100 [&_p]:inline [&_p]:m-0">{renderContent(section.title)}</h3>}
                        <div className="leading-relaxed text-base space-y-4">{renderContent(section.content)}</div>
                    </div>
                );
            })}
        </div>
    );
}
